// frontend/app/components/lessonPlans/LessonCoverPage.js
//
// The lesson landing view: what the lesson covers, what it aims at, what it
// assumes, and the teacher's tips.
//
// The toolkit shelf and the PREPARE / TEACH / ASSESS / ANALYZE workflow columns
// used to sit between the cards and are gone. Lessons are reached by describing
// one now rather than by picking a tile, so this page answers "is this the
// lesson I wanted?" and nothing else; both sections were inventory, answering a
// question the teacher had already stopped asking. The toolkit's Summary chip
// is the one control that came back — see askForSummary for why the Saras rail
// beside this page is not the same offer.
//
// Two things here are easy to mistake for noise. The cover measures itself
// rather than trusting the `wide` prop: the parent computes that from its own
// width, and the cover is ~312px narrower than the workspace (rail, gap, stage
// padding), so the prop puts the two-up layout on at widths where it does not
// fit. The prop survives only as the Jest fallback, because onLayout never
// fires there.
//
// And there is no Start button. The workspace footer is sticky and renders one
// at the cover index already; two identical primary buttons on one screen is a
// choice the teacher should never have to make.

import React, { useState } from "react";
import {
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

const COVER_MAX_WIDTH = 1240;
const COVER_WIDE_MIN = 900; // two-up objectives + prerequisites
const COVER_NARROW_MAX = 720; // everything stacks

const ListCard = ({ colors, title, icon, accent, items, ordered, fill, padding, t }) => {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return null;
  const translate = t || ((value) => value);

  return (
    <View
      style={[
        styles.card,
        // Only ever set on the two paired cards. On styles.card it would
        // collapse the tips card, which is a direct child of a column root.
        fill && styles.cardFill,
        { backgroundColor: colors.panelBg, borderColor: colors.border, padding },
      ]}
    >
      <View style={styles.cardHeader}>
        <MaterialCommunityIcons name={icon} size={16} color={accent} />
        <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>
          {translate(title)}
        </Text>
      </View>
      {list.map((item, index) => (
        <View key={`${title}-${index}`} style={styles.listRow}>
          {ordered ? (
            <View style={[styles.listNumber, { backgroundColor: `${accent}22` }]}>
              <Text style={[styles.listNumberText, { color: accent }]}>
                {index + 1}
              </Text>
            </View>
          ) : (
            <View style={[styles.listDot, { backgroundColor: accent }]} />
          )}
          <Text style={[styles.listText, { color: colors.textSecondary }]}>
            {item}
          </Text>
        </View>
      ))}
    </View>
  );
};

// Keeps the toolkit chip's amber lightbulb and its "Summary" label, because a
// teacher who learned the affordance on a topic tile should meet the same one
// here.
const SummaryButton = ({ colors, onPress, t }) => {
  const translate = t || ((value) => value);
  const label = translate("Summary");

  return (
    <View style={styles.summaryRow}>
      <TouchableOpacity
        onPress={onPress}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel={label}
        testID="cover-summary"
        style={[
          styles.summaryButton,
          { backgroundColor: colors.panelBg, borderColor: colors.border },
        ]}
      >
        <MaterialCommunityIcons
          name="lightbulb-outline"
          size={16}
          color={colors.warning}
        />
        <Text style={[styles.summaryLabel, { color: colors.textSecondary }]}>
          {label}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

const LessonCoverPage = ({
  lesson,
  colors,
  mode = "teacher",
  wide = true,
  onAsk,
  t,
}) => {
  const translate = t || ((value) => value);
  const { width: windowWidth } = useWindowDimensions();
  const [measuredWidth, setMeasuredWidth] = useState(0);

  // windowWidth would overstate the cover by the rail plus the stage padding,
  // so it is not a fallback here at all — `wide` is, and only until onLayout.
  const width = measuredWidth || (wide ? 1000 : 600);
  const isWide = width >= COVER_WIDE_MIN;
  const isNarrow = width < COVER_NARROW_MAX;
  const cardPadding = isNarrow ? 14 : 16;

  const tips = Array.isArray(lesson?.teacher_tips) ? lesson.teacher_tips : [];

  const hasObjectives = (lesson?.objectives || []).length > 0;
  const hasPrereqs = (lesson?.prerequisites || []).length > 0;
  // A solo list beside an empty 592px column reads as a missing card, so the
  // two-up row is only earned when there are two cards to put in it.
  const pairSideBySide = isWide && hasObjectives && hasPrereqs;

  // The tile's Summary asks Saras for a topic overview; from inside a lesson the
  // same question can be sharpened with what THIS lesson is trying to teach, and
  // the answer lands in the rail alongside rather than navigating anywhere. The
  // two outer phrasings are the tile's own strings so they share its i18n keys.
  //
  // That sharpening is why this outlived the toolkit that used to host it. The
  // rail's own prompts say "this lesson" without naming it, and handleAsk sends
  // the question text and nothing else, so from the cover they reach the model
  // with no idea which lesson is open. This is the only ask that carries one.
  const askForSummary = () => {
    if (!onAsk) return;
    const subject = lesson?.topic_name || lesson?.title || "";
    const objectives = (Array.isArray(lesson?.objectives) ? lesson.objectives : [])
      .filter(Boolean);

    const parts = [`${translate("Give me a quick overview of")} ${subject}.`];
    if (objectives.length > 0) {
      parts.push(`${translate("This lesson aims to")}: ${objectives.join("; ")}.`);
    }
    parts.push(
      translate(
        "Highlight key ideas, common misconceptions, and what to practice next."
      )
    );
    onAsk(parts.join(" "));
  };

  const objectivesCard = (
    <ListCard
      colors={colors}
      title="Learning objectives"
      icon="target"
      accent={colors.accentPrimary}
      items={lesson?.objectives}
      ordered
      fill={pairSideBySide}
      padding={cardPadding}
      t={t}
    />
  );

  const prerequisitesCard = (
    <ListCard
      colors={colors}
      title="Prerequisite knowledge"
      icon="stairs-up"
      accent={colors.info}
      items={lesson?.prerequisites}
      fill={pairSideBySide}
      padding={cardPadding}
      t={t}
    />
  );

  return (
    <View
      testID="cover-page"
      style={styles.container}
      onLayout={(event) => setMeasuredWidth(event.nativeEvent.layout.width)}
    >
      {lesson?.description ? (
        <View
          style={[
            styles.intro,
            { backgroundColor: colors.accentGlow, borderColor: colors.border },
          ]}
        >
          <Text style={[styles.introText, { color: colors.textPrimary }]}>
            {lesson.description}
          </Text>
        </View>
      ) : null}

      {/* Above the cards, not below the tips: the teacher who wants an overview
          wants it before reading, and it is the only action on the page. */}
      {onAsk ? (
        <SummaryButton colors={colors} onPress={askForSummary} t={t} />
      ) : null}

      {pairSideBySide ? (
        <View testID="cover-columns" style={styles.columns}>
          {objectivesCard}
          {prerequisitesCard}
        </View>
      ) : (
        <>
          {objectivesCard}
          {prerequisitesCard}
        </>
      )}

      {mode === "teacher" && tips.length > 0 ? (
        <ListCard
          colors={colors}
          title="Teaching tips"
          icon="lightbulb-on-outline"
          accent={colors.warning}
          items={tips}
          ordered
          padding={cardPadding}
          t={t}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    maxWidth: COVER_MAX_WIDTH,
    alignSelf: "center",
    paddingBottom: 24,
  },
  intro: {
    borderRadius: 14,
    borderWidth: 1,
    padding: 16,
    marginBottom: 16,
  },
  introText: { fontSize: 15, lineHeight: 24, maxWidth: 760 },

  // A row so the button takes its own width rather than the container's.
  summaryRow: { flexDirection: "row", marginBottom: 16 },
  summaryButton: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 10,
    borderWidth: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    gap: 8,
  },
  summaryLabel: { fontSize: 13, fontWeight: "700", marginLeft: 8 },

  columns: {
    flexDirection: "row",
    gap: 12,
    alignItems: "stretch",
    marginBottom: 12,
  },

  card: {
    borderRadius: 14,
    borderWidth: 1,
    marginBottom: 12,
  },
  cardFill: { flex: 1, marginBottom: 0 },
  cardHeader: { flexDirection: "row", alignItems: "center", marginBottom: 10, gap: 8 },
  cardTitle: {
    fontSize: 11,
    fontWeight: "800",
    letterSpacing: 0.6,
    textTransform: "uppercase",
    marginLeft: 8,
  },
  listRow: { flexDirection: "row", alignItems: "flex-start", marginBottom: 8 },
  listDot: { width: 6, height: 6, borderRadius: 3, marginTop: 8, marginRight: 10 },
  listNumber: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
    marginTop: 1,
  },
  listNumberText: { fontSize: 11, fontWeight: "800" },
  listText: { fontSize: 14, lineHeight: 22, flex: 1 },
});

export default LessonCoverPage;
